import React, { FC } from 'react';
import { View } from 'react-native';
import { WebView } from 'react-native-webview';

import ColorWrapper, { WithColor } from '@styles/colors';
import { ImageDocumentStoreItem, ImageDocumentStoreItemWithAngle } from '@v2/components/imageHandling/types/types.d';
import DiscardChangesPopup from '@v2/components/imageHandling/components/Cropper/components/DiscardChangesPopup';
import ChooseCropModeModal from '@v2/components/imageHandling/components/Cropper/components/ChooseCropModeModal';
import ModalWrapper from '@components/modal/ModalWrapper';

// Import HTML file content
import HtmlFile from './index.html';
import { useManageCropperWeb } from '@v2/components/imageHandling/components/Cropper/hooks/useManageCropperWeb';
import { scale } from '@components/frame';
import { CROPPER_WEB_HEADER_HEIGHT, CROPPER_WEB_MAX_WIDTH } from '@v2/components/imageHandling/constants';
import { useManageChooseCropModeModal } from '@v2/components/imageHandling/components/Cropper/hooks/useManageChooseCropModeModal';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

export interface CropperWebProps extends WithColor {
  image: ImageDocumentStoreItem;
  angle?: number;
  onSave: (updatedImage: ImageDocumentStoreItemWithAngle) => void;
  onBack: () => void;
  onDelete?: () => void;
  width?: string;
  shouldSaveOnDoneClickIfNotDirty?: boolean;
  shouldLoadDocument?: boolean;
  isNewImage?: boolean;
  withoutAnimation?: boolean;
}

const CropperWeb: FC<CropperWebProps> = ({
  image,
  angle,
  onSave,
  onBack,
  onDelete,
  C,
  width = '100%',
  shouldSaveOnDoneClickIfNotDirty = true,
  shouldLoadDocument = false,
  isNewImage = false,
  withoutAnimation = false,
}) => {
  const {
    onLoad,
    onMessage,
    showDiscardChangesPopup,
    triggerDiscardChangesPopup,
    webView,
    showChooseCropModeModalOpen,
    setShowChooseCropModeModalOpen,
    pendingCropData,
    setPendingCropData,
    processCropData,
  } = useManageCropperWeb({
    image,
    onBack,
    onSave,
    angle,
    onDelete,
    shouldSaveOnDoneClickIfNotDirty,
    shouldLoadDocument,
    isNewImage,
  });

  const { config, onCloseShowChooseCropModeModalOpen } = useManageChooseCropModeModal({
    closeCrop: onBack,
    image,
    pendingCropData,
    processCropData,
    setPendingCropData,
    setShowChooseCropModeModalOpen,
  });

  return (
    // wrap everything in ModalWrapper since we need DiscardChangesPopup and ChooseCropModeModal be nested inside same portal
    <ModalWrapper isVisible={true} C={C} onBackdropPress={() => {}} withoutAnimation={withoutAnimation}>
      <DiscardChangesPopup {...{ showDiscardChangesPopup, triggerDiscardChangesPopup, cancelCrop: onBack, C }} />
      <ChooseCropModeModal
        isVisible={showChooseCropModeModalOpen}
        onClose={onCloseShowChooseCropModeModalOpen}
        C={C}
        config={config}
        containerStyle={{
          top: scale(CROPPER_WEB_HEADER_HEIGHT),
          left: '50%',
          transform: [{ translateX: -scale(CROPPER_WEB_MAX_WIDTH / 2) }],
          width: scale(CROPPER_WEB_MAX_WIDTH),
          paddingRight: scale(85),
        }}
      />
      <View testID={TEST_IDS.documentsCropperOverlay.root} style={{ height: '100%' as any, width: width as any, zIndex: 1000000, position: 'absolute' }}>
        <WebView source={{ html: HtmlFile }} ref={webView} onMessage={onMessage} onLoad={onLoad} originWhitelist={['*']} scrollEnabled={false} />
      </View>
    </ModalWrapper>
  );
};

export default ColorWrapper(CropperWeb);
